<script setup lang="ts">
/**
 * AppDatePicker
 *
 * Wrapper around PrimeVue DatePicker that adds Save / Cancel footer buttons
 * **only** when a time picker is shown (showTime / show-time prop).
 * Date-only pickers pass through with no extra UI.
 *
 * Behaviour for time-enabled pickers:
 *  - Panel opens with current date/time pre-selected when modelValue is null.
 *  - A transparent full-screen backdrop is rendered behind the panel while it
 *    is open.  The backdrop absorbs pointer events so PrimeVue's
 *    outside-click handler never fires and the panel stays open until the
 *    user explicitly clicks Save, Cancel, or presses Escape.
 *
 * All PrimeVue DatePicker props are forwarded via v-bind="$attrs".
 */
import DatePicker from 'primevue/datepicker';
import Button from 'primevue/button';
import { useLocale } from '@/shared/composables/useLocale';

const props = defineProps<{
  /** The bound date value (v-model) */
  modelValue: Date | Date[] | null | undefined;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date | Date[] | null | undefined): void;
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const { t } = useLocale();

/** Show footer only when the time picker is active */
const hasTimePicker = computed(() => {
  // Vue normalises both show-time and showTime into attrs
  return attrs['show-time'] === true || attrs['show-time'] === '' || attrs['showTime'] === true || attrs['showTime'] === '';
});

/** Internal (draft) value — used only when time picker is active */
const draft = ref<Date | Date[] | null | undefined>(props.modelValue);

/** Ref to underlying DatePicker for programmatic hide */
const datepickerRef = ref<InstanceType<typeof DatePicker> | null>(null);

/**
 * True while the DatePicker panel is open.
 * Controls the backdrop that prevents outside-click dismissal.
 * Only set to true after we have successfully read the panel z-index.
 */
const isPanelOpen = ref(false);

/**
 * z-index for the backdrop div — set dynamically to (panel z-index − 1) so
 * it always sits just below the floating panel.
 */
const backdropZIndex = ref(0);

/** Keyboard listener installed while the panel is open; handles Escape → Cancel. */
let escKeyListener: ((e: KeyboardEvent) => void) | null = null;

/** Sync draft when parent value changes externally */
watch(() => props.modelValue, (v: Date | Date[] | null | undefined) => {
  draft.value = v;
});

/**
 * When the panel opens:
 *  1. Pre-populate draft with now when modelValue is null/undefined.
 *  2. After the panel has rendered, read its z-index and show the backdrop
 *     one level below it so it absorbs all outside pointer events.
 *  3. Install an Escape-key listener that triggers cancel().
 */
function onShow() {
  if (!hasTimePicker.value) return;

  // Pre-populate with current date/time when no value is set
  draft.value = props.modelValue instanceof Date
    ? new Date(props.modelValue.getTime())
    : Array.isArray(props.modelValue)
      ? props.modelValue.map((d: Date | null) => d ? new Date(d.getTime()) : d)
      : new Date(); // null/undefined → current date + time

  // After the panel DOM is rendered, detect its z-index and show the backdrop
  nextTick(() => {
    const dp = datepickerRef.value as any;
    // PrimeVue v4 exposes the panel element under different names depending
    // on the version — try the most common ones.
    const panelEl: HTMLElement | null =
      (dp?.overlay instanceof HTMLElement ? dp.overlay : null) ??
      (dp?.container instanceof HTMLElement ? dp.container : null) ??
      (dp?.$refs?.panel instanceof HTMLElement ? dp.$refs.panel : null) ??
      null;

    if (panelEl) {
      const z = parseInt(
        panelEl.style.zIndex || getComputedStyle(panelEl).zIndex,
        10,
      );
      if (!isNaN(z) && z > 0) {
        backdropZIndex.value = z - 1;
        isPanelOpen.value = true;
        // Backdrop is now visible — outside clicks are absorbed by it and
        // will never reach PrimeVue's document-level outside-click handler.
        return;
      }
    }
    // z-index detection failed — degrade gracefully: no backdrop is shown
    // and the panel will still close on outside click (old behaviour), but
    // at least there is no flicker.
  });

  // Escape key while panel is open → same as Cancel
  escKeyListener = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      cancel();
    }
  };
  document.addEventListener('keydown', escKeyListener, { capture: true });
}

/** When the panel hides (Save / Cancel / Escape), remove backdrop + listener. */
function onHide() {
  isPanelOpen.value = false;

  if (escKeyListener) {
    document.removeEventListener('keydown', escKeyListener, { capture: true });
    escKeyListener = null;
  }
}

/** Guard against unmounting while the panel is still open — ensures the
 *  document-level keydown listener is always removed and backdrop state is
 *  reset, preventing the Escape handler from leaking into other components. */
onUnmounted(() => {
  onHide();
});

function onDraftUpdate(val: Date | Date[] | (Date | null)[] | null | undefined) {
  if (hasTimePicker.value) {
    // Normalize: filter nulls from arrays only for the time-picker draft,
    // and only when the range is fully selected (no pending null slots).
    let normalized: Date | Date[] | null | undefined;
    if (Array.isArray(val)) {
      const hasNull = (val as (Date | null)[]).some(v => v == null);
      if (hasNull) {
        // Range selection still in progress — keep as-is
        normalized = val as Date[];
      } else {
        const filtered = (val as (Date | null)[]).filter(Boolean) as Date[];
        normalized = filtered.length > 0 ? filtered : null;
      }
    } else {
      normalized = val ?? null;
    }
    // Only update draft; parent model is committed on Save
    draft.value = normalized;
  } else {
    // No time picker — pass arrays through unchanged so in-progress
    // range selections ([startDate, null]) are not collapsed.
    const normalized = Array.isArray(val) ? val : (val ?? null);
    emit('update:modelValue', normalized);
  }
}

function save() {
  emit('update:modelValue', draft.value);
  hidePanel();
}

function cancel() {
  draft.value = props.modelValue;
  hidePanel();
}

function hidePanel() {
  const dp = datepickerRef.value as any;
  if (dp?.overlayVisible !== undefined) {
    dp.overlayVisible = false;
  }
}
</script>

<template>
  <!--
    Transparent backdrop rendered while the time-picker panel is open.
    z-index is set to (panel z-index − 1) so the panel floats above it.
    @click.stop / @mousedown.stop absorb all outside pointer events, preventing
    PrimeVue's document-level outside-click handler from ever running.
    The panel stays open until Save, Cancel, or Escape is used.
  -->
  <Teleport
    v-if="hasTimePicker && isPanelOpen"
    to="body"
  >
    <div
      class="fixed inset-0"
      :style="{ zIndex: backdropZIndex }"
      @click.stop
      @mousedown.stop
    />
  </Teleport>

  <DatePicker
    ref="datepickerRef"
    :model-value="hasTimePicker ? draft : modelValue"
    v-bind="$attrs"
    @show="onShow"
    @hide="onHide"
    @update:model-value="onDraftUpdate"
  >
    <template
      v-if="hasTimePicker"
      #footer
    >
      <div class="flex items-center justify-end gap-2 px-3 py-2 border-t border-gray-200">
        <Button
          :label="t('common.cancel')"
          severity="secondary"
          text
          size="small"
          class="text-sm!"
          @click="cancel"
        />
        <Button
          :label="t('common.save')"
          size="small"
          class="text-sm!"
          @click="save"
        />
      </div>
    </template>
  </DatePicker>
</template>
